import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, Monitor, Smartphone, MapPin, LogOut, AlertTriangle, Clock, Activity } from 'lucide-react';
import { 
  getUserActiveSessions, 
  forceLogoutSession, 
  subscribeToUserSessions,
  formatSessionDuration,
  formatTimeSinceActivity 
} from '../lib/sessionService';

const UserSessionViewer = ({ user, isOpen, onClose, onForceLogout }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [sessionToLogout, setSessionToLogout] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (isOpen && user) {
      setError('');
      setSuccess('');
      setShowConfirmation(false);
      setSessionToLogout(null);
      loadUserSessions();
    }
  }, [isOpen, user]);

  // Subscribe to real-time session updates
  useEffect(() => {
    if (!isOpen || !user) return;

    const unsubscribe = subscribeToUserSessions(user.id, (updatedSessions) => {
      setSessions(updatedSessions);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isOpen, user]);

  const loadUserSessions = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userSessions = await getUserActiveSessions(user.id);
      setSessions(userSessions);
    } catch (error) {
      console.error('Error loading user sessions:', error);
      setError('Error al cargar las sesiones del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogout = (session) => {
    if (session.userId === currentUser?.uid && session.isCurrentSession) {
      setError('No puedes cerrar tu propia sesión desde aquí');
      return;
    }

    setSessionToLogout(session);
    setShowConfirmation(true);
  };

  const confirmLogout = async () => {
    if (!sessionToLogout) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setShowConfirmation(false);

    try {
      // Force logout session using sessionService
      await forceLogoutSession(sessionToLogout.id, currentUser?.uid);

      // Update local sessions state
      setSessions(prev => prev.filter(s => s.id !== sessionToLogout.id));

      // Call parent callback if provided
      if (onForceLogout) {
        await onForceLogout(sessionToLogout.id, sessionToLogout.userId);
      }

      setSuccess(`Sesión cerrada exitosamente para ${sessionToLogout.device}`);
      
    } catch (error) {
      console.error('Error forcing logout:', error);
      setError('Error al cerrar la sesión');
    } finally {
      setLoading(false);
      setSessionToLogout(null);
    }
  };

  const getDeviceIcon = (device) => {
    if (device.toLowerCase().includes('mobile') || device.toLowerCase().includes('android') || device.toLowerCase().includes('ios')) {
      return <Smartphone className="h-4 w-4 text-gray-500" />;
    }
    return <Monitor className="h-4 w-4 text-gray-500" />;
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Use session service formatting functions
  const getSessionDuration = formatSessionDuration;
  const getTimeSinceActivity = formatTimeSinceActivity;

  const getLocationFromIP = (ipAddress) => {
    // In a real app, you would use a geolocation service
    // For now, return mock locations based on IP patterns
    if (ipAddress.startsWith('192.168.')) {
      return 'Red Local';
    } else if (ipAddress.startsWith('10.')) {
      return 'Red Corporativa';
    } else {
      return 'Ubicación Externa';
    }
  };

  const userSessions = sessions || [];

  if (!isOpen || !user) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-6 w-6 text-red-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Sesiones Activas</h2>
                  <p className="text-sm text-gray-600">
                    {user.displayName || 'Usuario'} ({user.email})
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Error and Success Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-4">
                {success}
              </div>
            )}

            {/* Session Info */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Sesiones Activas ({userSessions.length})
                </h3>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>Actualización en tiempo real</span>
                </div>
              </div>

              {userSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p>No hay sesiones activas para este usuario</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dispositivo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Inicio de Sesión
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Última Actividad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP / Ubicación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duración
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userSessions.map((session) => (
                        <tr 
                          key={session.id}
                          className={session.isCurrentSession ? 'bg-blue-50' : ''}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              {getDeviceIcon(session.device)}
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {session.device}
                                  {session.isCurrentSession && (
                                    <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                      Sesión Actual
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDateTime(session.loginTime)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{formatDateTime(session.lastActivity)}</div>
                              <div className="text-xs text-gray-400">
                                {getTimeSinceActivity(session.lastActivity)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              <div>
                                <div>{session.ipAddress}</div>
                                <div className="text-xs text-gray-400">
                                  {getLocationFromIP(session.ipAddress)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getSessionDuration(session.loginTime)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {!session.isCurrentSession ? (
                              <button
                                onClick={() => handleForceLogout(session)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-900 inline-flex items-center space-x-1 disabled:opacity-50"
                              >
                                <LogOut className="h-4 w-4" />
                                <span>Cerrar Sesión</span>
                              </button>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                Sesión actual
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Security Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">Información de Seguridad</p>
                  <p className="text-yellow-700 mt-1">
                    Cerrar sesiones remotas desconectará inmediatamente al usuario de esos dispositivos. 
                    La sesión actual no puede ser cerrada desde aquí por seguridad.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && sessionToLogout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
                <h3 className="text-lg font-medium text-gray-900">Confirmar Cierre de Sesión</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  ¿Está seguro de cerrar esta sesión?
                </p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="flex items-center space-x-2 mb-1">
                    {getDeviceIcon(sessionToLogout.device)}
                    <span className="font-medium">{sessionToLogout.device}</span>
                  </div>
                  <div className="text-gray-600">
                    IP: {sessionToLogout.ipAddress}<br/>
                    Inicio: {formatDateTime(sessionToLogout.loginTime)}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  El usuario será desconectado inmediatamente y deberá iniciar sesión nuevamente.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmLogout}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    'Cerrar Sesión'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserSessionViewer;